import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal, Breadcrumbs, CtaBand, TrustChips } from "@/components/Blocks";
import { BUSINESS } from "@/data/site";
import { SERVICES } from "@/data/services";

export default function CityPage({ city: c }) {
    const crumbs = [
        { name: "Home", path: "/" },
        { name: c.county, path: `/${c.countySlug}` },
        { name: `${c.name}, FL`, path: `/${c.slug}` },
    ];
    return (
        <div>
            <Seo title={c.metaTitle} description={c.metaDesc} path={`/${c.slug}`} schemas={[breadcrumbSchema(crumbs)]} />

            <section className="bg-slate-950 text-white dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                    <Breadcrumbs items={crumbs} />
                    <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">{c.county}, Florida</p>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">{c.h1}</h1>
                    <p className="mt-4 text-slate-200 leading-relaxed max-w-3xl">{c.intro[0]}</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <div className="grid lg:grid-cols-[1fr_340px] gap-10">
                    <div>
                        {c.intro.slice(1).map((p, i) => (
                            <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
                        ))}

                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-7 mb-4">
                            Appliance issues we repair in {c.name}
                        </h2>
                        <ul className="space-y-3" data-testid="city-common-calls">
                            {c.commonCalls.map((call) => (
                                <li key={call} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                                    <CheckCircle2 className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                    <span>{call}</span>
                                </li>
                            ))}
                        </ul>

                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-10 mb-4">
                            Appliance repair services in {c.name}
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {SERVICES.map((s) => (
                                <Link key={s.slug} to={`/${s.slug}`}
                                    className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-red-400 hover:shadow-md transition-all"
                                    data-testid={`city-service-${s.slug}`}>
                                    <s.icon className="h-5 w-5 text-red-600 shrink-0" />
                                    <span className="font-heading font-bold text-slate-900 text-sm group-hover:text-red-600">
                                        {s.name} in {c.name}
                                    </span>
                                </Link>
                            ))}
                        </div>

                        <p className="mt-7 text-sm text-slate-500">
                            Also serving nearby: {c.nearby.join(", ")} — and all of{" "}
                            <Link to={`/${c.countySlug}`} className="text-red-600 font-semibold hover:underline">{c.county}</Link>.
                        </p>
                    </div>

                    <aside>
                        <Reveal>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sticky top-28" data-testid="city-sidebar">
                                <TrustChips />
                            </div>
                        </Reveal>
                    </aside>
                </div>
            </section>

            <CtaBand
                title={<>Need appliance repair in {c.name}? <span className="text-amber-400">Call Appliance Mania.</span></>}
                sub={`In-home appliance repair in ${c.name}, FL — most appointments can be scheduled within about 24 hours, subject to availability. $155 service call waived with completed repair. ${BUSINESS.phone}.`}
                testid={`city-cta-${c.slug}`}
            />
        </div>
    );
}
